import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'

import type { BannerCTAProps } from '~/components/atoms/BannerCTA/BannerCTA'
import BannerCTA from '~/components/atoms/BannerCTA/BannerCTA'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Container from '~/components/atoms/Container/Container'
import type { DocumentDownloadListProps } from '~/components/atoms/DocumentDownloadList/DocumentDownloadList'
import DocumentDownloadList from '~/components/atoms/DocumentDownloadList/DocumentDownloadList'
import type { FullWidthImageProps } from '~/components/atoms/FullWidthImage/FullWidthImage'
import type { PressContactDetailsProps } from '~/components/atoms/PressContactDetails/PressContactDetails'
import AsSeenIn from '~/components/blocks/AsSeenIn/AsSeenIn'
import type { FeaturedArticlesProps } from '~/components/blocks/FeaturedArticles/FeaturedArticles'
import PressHeader from '~/components/press/PressHeader/PressHeader'
import PressNavigation from '~/components/press/PressNavigation/PressNavigation'
import PressRelatedArticles from '~/components/press/PressRelatedArticles/PressRelatedArticles'
import { resolveBlock } from '~/components/serializers'
import {
  featuredArticles,
  getBody,
  image,
  internationalizedField,
  link,
  myPortfolioBanner,
  pageLanguageFilter,
  pressReleases,
} from '~/schemas/common'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb } from '~/utils/breadcrumb'
import { buildTranslatedUrlsQuery } from '~/utils/getHreflangUrls'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { createLangStaticPaths, supportedLocales } from '~/utils/langPaths'
import { urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

export interface PageData {
  title: string
  summary: any
  pressReleases: DocumentDownloadListProps
  featuredArticles: FeaturedArticlesProps
  pressContactDetails: PressContactDetailsProps
  pressContact: PressContactDetailsProps
  fullWidthImage: FullWidthImageProps
  body: any
  bannerCTA: BannerCTAProps
  breadcrumb: BreadcrumbProps['list']
  pressCategories: { name: string; slug: string; order: number }[]
}

const Press: NextPage<PageData> = (props: PageData) => {
  const {
    title,
    pressReleases,
    featuredArticles,
    bannerCTA,
    summary,
    breadcrumb,
    pressCategories,
    body,
  } = props

  return (
    <>
      <Breadcrumb list={breadcrumb} />
      <PressHeader
        isPressCategory={true}
        name={title}
        includeAsSeenIn
        summary={summary}
      />
      <Container>
        <AsSeenIn noText />
      </Container>
      {pressCategories?.length > 0 && (
        <PressNavigation
          marginTop
          featuredCategories={pressCategories.sort(
            (a, b) => (a?.order || 0) - (b?.order || 0)
          )}
        />
      )}
      <PressRelatedArticles
        title={featuredArticles?.title}
        articles={featuredArticles?.articles.slice(0, 12)}
      />
      {body && (
        <div className={styles.body}>
          {/* Filter by type not being featuredArticles */}
          {body
            .filter(
              (item: any) =>
                !(
                  item._type == 'collectionSelector' &&
                  item.value === 'featuredArticles'
                ) &&
                !(
                  item._type == 'collectionSelector' &&
                  item.value === 'pressContactDetails'
                )
            )
            .map((block: any, i: any) => resolveBlock(block, i))}
        </div>
      )}
      {pressReleases && <DocumentDownloadList {...pressReleases} />}
      <BannerCTA {...bannerCTA} />
    </>
  )
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<any>> {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity(context)
    const lang = (context.params?.lang as Locale) || 'en'
    const messages = await loadMessages(lang)

    const query = `*[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0]{
          ${urlQuery},
          ${buildTranslatedUrlsQuery([...supportedLocales])},
          title,
          summary,
          "fullWidthImage": body[_type == 'fullWidthImage'][0]{${image}},
          "bannerCTA": body[_type == 'bannerCTA'][0]{
            ...,
            ${link},
          },
          ${getBody(lang)},
          ${pressReleases},
          ${featuredArticles},
          hasGoogleReviews,
          fullGoogleReviews,
          "pressCategories": *[_type == 'category' && type == 'press'] {
            "name": ${internationalizedField('name')},
            "slug": slug.current,
            "order": categoryOrder
          },
          ${myPortfolioBanner}
          "pressContactDetails": body[_type == 'collectionSelector' && value == 'pressContactDetails'][0]
      }`

    const data = await client.fetch(query, {
      slug: 'press',
      lang: lang,
    })

    const breadcrumbPieces = ['Press and Media']
    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      breadcrumbPieces,
      '',
      undefined,
      messages,
      lang
    )

    return {
      props: {
        ...data,
        breadcrumb: breadcrumb,
        meta: {
          title: `Press and Media | MyArtBroker`,
        },
      },
    }
  } catch (error) {
    console.error('❌ Press getStaticPropsOriginal error:', error)
    return { props: {} }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () =>
  createLangStaticPaths()

export default Press
